import { useEffect, useState } from 'react'
import type { GuideShot } from './guideTypes'
import { pickLang } from './guideTypes'
import GuideHtmlMock from './GuideHtmlMock'

interface Props {
  shot: GuideShot
  language: string
  className?: string
  /** Force HTML mock even if src exists (debug) */
  preferHtml?: boolean
}

/**
 * Prefer realistic HTML screen replicas.
 * Only show a file image when it actually loads; otherwise always use the app-like mock.
 */
export default function GuideScreenshot({ shot, language, className = '', preferHtml = true }: Props) {
  const caption = pickLang(shot.caption, language)
  const [imgFailed, setImgFailed] = useState(false)
  const [imgLoaded, setImgLoaded] = useState(false)

  useEffect(() => {
    setImgFailed(false)
    setImgLoaded(false)
  }, [shot.src])

  // By default use HTML replicas that match SmartAccount UI.
  // Set preferHtml=false and provide shot.src to use real screenshots when available.
  const tryImage = Boolean(shot.src) && !preferHtml && !imgFailed
  const showImage = tryImage && imgLoaded

  return (
    <figure className={`group ${className}`}>
      <div className="relative overflow-hidden rounded-2xl border border-slate-200/20 bg-slate-100 shadow-2xl ring-1 ring-white/10">
        {!showImage && <GuideHtmlMock shot={shot} language={language} />}

        {tryImage && (
          <img
            src={shot.src}
            alt={caption}
            className={`block w-full object-cover transition duration-700 group-hover:scale-[1.02] ${
              showImage ? 'relative' : 'absolute inset-0 opacity-0 pointer-events-none'
            }`}
            style={{ aspectRatio: '16 / 10' }}
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgFailed(true)}
          />
        )}
      </div>
      <figcaption className="mt-2 text-center text-xs text-teal-100/70">{caption}</figcaption>
    </figure>
  )
}
